import { useState, useCallback, useRef } from 'react';
import HealthAgentRouter from '../services/perplexityAgents';

// Custom hook for Perplexity health agents
export const usePerplexityAgents = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState('MainHealthAgent');
  const [agentHistory, setAgentHistory] = useState([]);
  const [error, setError] = useState(null);
  
  // Create router instance (persisted across re-renders)
  const routerRef = useRef(new HealthAgentRouter());

  // Process message through agent system
  const processMessage = useCallback(async (message, context = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      // Route message to appropriate agent
      const response = await routerRef.current.routeToAgent(message, context);
      
      // Update current agent tracking
      setCurrentAgent(response.agent);
      
      // Add to agent history
      setAgentHistory(prev => [...prev, {
        id: Date.now(),
        userMessage: message,
        agentResponse: response,
        timestamp: new Date()
      }]);
      
      return {
        success: true,
        response,
        agent: response.agent,
        canEscalate: response.canEscalate,
        nextSuggestedAgent: response.nextSuggestedAgent
      };
      
    } catch (err) {
      console.error('Agent processing error:', err);
      setError(err.message);
      
      return {
        success: false,
        error: err.message,
        response: {
          content: "I'm sorry, I encountered an issue processing your request. Please try again or contact your healthcare provider if this is urgent.",
          agent: 'ErrorHandler',
          timestamp: new Date()
        }
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get agent history for current session
  const getAgentHistory = useCallback(() => {
    return routerRef.current.agentHistory;
  }, []);

  // Clear agent history
  const clearAgentHistory = useCallback(() => {
    routerRef.current.agentHistory = [];
    setAgentHistory([]);
  }, []);

  // Force specific agent (useful for testing or specific workflows)
  const forceAgent = useCallback(async (message, agentType, context = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const intent = { type: agentType, priority: 'medium', confidence: 1.0 };
      const agent = routerRef.current.selectAgent(intent, context);
      
      const response = await agent.process(message, context);
      
      setCurrentAgent(response.agent);
      setAgentHistory(prev => [...prev, {
        id: Date.now(),
        userMessage: message,
        agentResponse: response,
        forced: true,
        timestamp: new Date()
      }]);
      
      return {
        success: true,
        response,
        agent: response.agent,
        forced: true
      };
      
    } catch (err) {
      console.error('Forced agent processing error:', err);
      setError(err.message);
      
      return {
        success: false,
        error: err.message
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get available agent types
  const getAvailableAgents = useCallback(() => {
    return [
      { type: 'general', name: 'MainHealthAgent', description: 'General health guidance and routing' },
      { type: 'emergency', name: 'EmergencyAgent', description: 'Emergency medical assistance' },
      { type: 'medication', name: 'MedicationAgent', description: 'Medication management and information' },
      { type: 'vitals', name: 'VitalsMonitoringAgent', description: 'Vital signs monitoring and analysis' },
      { type: 'nutrition', name: 'NutritionAgent', description: 'Nutrition and dietary guidance' },
      { type: 'mental_health', name: 'MentalHealthAgent', description: 'Mental health and emotional support' },
      { type: 'general_health', name: 'HealthResearchAgent', description: 'Health research and medical information' }
    ];
  }, []);

  // Classify user intent without processing
  const classifyIntent = useCallback((message) => {
    return routerRef.current.classifyIntent(message);
  }, []);

  // Get agent statistics
  const getAgentStats = useCallback(() => {
    const history = routerRef.current.agentHistory;
    const agentCounts = {};
    const totalMessages = history.length;
    
    history.forEach(entry => {
      agentCounts[entry.agent] = (agentCounts[entry.agent] || 0) + 1;
    });
    
    return {
      totalMessages,
      agentUsage: agentCounts,
      currentAgent,
      lastActivity: history.length > 0 ? history[history.length - 1].timestamp : null
    };
  }, [currentAgent]);

  return {
    // Core functions
    processMessage,
    forceAgent,
    classifyIntent,
    
    // State
    isLoading,
    currentAgent,
    agentHistory,
    error,
    
    // Utility functions
    getAgentHistory,
    clearAgentHistory,
    getAvailableAgents,
    getAgentStats
  };
};

// Specialized hooks for specific agent types
export const useEmergencyAgent = () => {
  const { forceAgent, isLoading, error } = usePerplexityAgents();
  
  const handleEmergency = useCallback(async (message, context = {}) => {
    return await forceAgent(message, 'emergency', {
      ...context,
      urgency: 'critical',
      needsRecentInfo: true
    });
  }, [forceAgent]);
  
  return { handleEmergency, isLoading, error };
};

export const useMedicationAgent = () => {
  const { forceAgent, isLoading, error } = usePerplexityAgents();
  
  const handleMedicationQuery = useCallback(async (message, context = {}) => {
    return await forceAgent(message, 'medication', {
      ...context,
      needsRecentInfo: true
    });
  }, [forceAgent]);
  
  return { handleMedicationQuery, isLoading, error };
};

export const useNutritionAgent = () => {
  const { forceAgent, isLoading, error } = usePerplexityAgents();
  
  const handleNutritionQuery = useCallback(async (message, context = {}) => {
    return await forceAgent(message, 'nutrition', context);
  }, [forceAgent]);
  
  return { handleNutritionQuery, isLoading, error };
};

// Helper function to format agent responses for display
export const formatAgentResponse = (response) => {
  if (!response) return null;
  
  // Extract citations from response if available
  const extractCitations = (content) => {
    const citationRegex = /\[(\d+)\]/g;
    const citations = [];
    let match;
    
    while ((match = citationRegex.exec(content)) !== null) {
      citations.push({
        number: parseInt(match[1]),
        position: match.index
      });
    }
    
    return citations;
  };

  // Extract sources if provided by Perplexity API
  const extractSources = (apiResponse) => {
    // Perplexity API might include sources in the response
    if (apiResponse.citations) {
      return apiResponse.citations.map((citation, index) => ({
        id: index + 1,
        title: citation.title || 'Medical Source',
        url: citation.url || '#',
        domain: citation.url ? new URL(citation.url).hostname : 'Verified Source',
        snippet: citation.snippet || ''
      }));
    }
    return [];
  };

  const citations = extractCitations(response.content);
  const sources = extractSources(response);
  
  return {
    id: Date.now(),
    text: response.content,
    sender: 'nurse', // Maps to existing UI expectation
    timestamp: response.timestamp || new Date(),
    type: 'agent-response',
    agent: response.agent,
    model: response.model,
    canEscalate: response.canEscalate,
    nextSuggestedAgent: response.nextSuggestedAgent,
    citations: citations,
    sources: sources,
    metadata: {
      confidence: response.context?.confidence,
      priority: response.context?.priority,
      domainFiltered: response.context?.domainFiltered || false,
      searchContextSize: response.context?.searchContextSize || 'medium'
    }
  };
};

export default usePerplexityAgents; 